/**
 * Customer shop product splash (Announcements popup campaign).
 * Frequency `once` is keyed by Firebase Auth uid + campaignId
 * (`users/{uid}.productSplashSeen`). daily / always skip that permanent gate.
 */

import {
    isSeenForDisplayFrequency,
    normalizeAnnouncementDisplayFrequency,
} from './announcementDisplayFrequency';

export const PRODUCT_SPLASH_KIND = 'productSplash';
export const PRODUCT_SPLASH_SEEN_FIELD = 'productSplashSeen';
export const PRODUCT_SPLASH_LOCAL_PREFIX = 'lsc_product_splash_seen_';

export function sanitizeProductSplashCampaignId(raw) {
    return String(raw || '')
        .trim()
        .replace(/[^A-Za-z0-9_-]/g, '_')
        .slice(0, 80);
}

export function generateProductSplashCampaignId(nowMs = Date.now()) {
    const rand = Math.random().toString(36).slice(2, 8);
    return sanitizeProductSplashCampaignId(`ps${Number(nowMs).toString(36)}${rand}`);
}

export function isProductSplashAnnouncement(data) {
    if (!data || typeof data !== 'object') return false;
    if (data.productSplash === true) return true;
    return data.kind === PRODUCT_SPLASH_KIND;
}

/** campaignId on the doc, else the announcement document id. */
export function getProductSplashCampaignId(data) {
    if (!data || typeof data !== 'object') return '';
    return sanitizeProductSplashCampaignId(data.campaignId || data.id);
}

export function buildProductSplashCtaPath(sku) {
    const code = String(sku || '').trim();
    if (!code) return '';
    return `/product/${encodeURIComponent(code)}`;
}

export function lockProductSplashChannels() {
    return { popup: true, toast: false, banner: false };
}

export function isProductSplashShopPath(pathname) {
    const p = String(pathname || '');
    if (p === '/' || p === '') return true;
    if (p === '/products') return true;
    if (p.startsWith('/product/')) return true;
    return false;
}

/** True when startDate is in the future (ISO YYYY-MM-DD). Empty startDate is ready. */
export function isProductSplashStartPending(startDate, isoDate) {
    const start = String(startDate || '').trim();
    if (!start) return false;
    const today = String(isoDate || '').trim();
    if (!today) return false;
    return start > today;
}

export function isCampaignSeenInFirestore(firestoreSeen, campaignId) {
    const id = sanitizeProductSplashCampaignId(campaignId);
    if (!id || !firestoreSeen || typeof firestoreSeen !== 'object') return false;
    const entry = firestoreSeen[id];
    if (!entry) return false;
    if (entry === true) return true;
    if (typeof entry === 'object' && (entry.seenAt || entry.seen === true)) return true;
    return false;
}

export function productSplashLocalStorageKey(uid) {
    const id = String(uid || '').trim();
    if (!id) return '';
    return `${PRODUCT_SPLASH_LOCAL_PREFIX}${id}`;
}

export function readProductSplashSeenLocal(uid) {
    const key = productSplashLocalStorageKey(uid);
    if (!key || typeof localStorage === 'undefined') return {};
    try {
        const parsed = JSON.parse(localStorage.getItem(key) || '{}');
        if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
        return parsed;
    } catch {
        return {};
    }
}

export function writeProductSplashSeenLocal(uid, campaignId, nowIso) {
    const key = productSplashLocalStorageKey(uid);
    const id = sanitizeProductSplashCampaignId(campaignId);
    if (!key || !id || typeof localStorage === 'undefined') return;
    const next = { ...readProductSplashSeenLocal(uid), [id]: nowIso || true };
    try {
        localStorage.setItem(key, JSON.stringify(next));
    } catch {
        /* ignore quota */
    }
}

/**
 * Once-only resolver for a product splash campaign.
 * Firestore `productSplashSeen[campaignId]` is the gate. localStorage is not.
 * Guests (no uid) never show. Missing SKU / future startDate / non-shop path hide.
 */
export function shouldShowProductSplashOnce(input = {}) {
    const uid = String(input.uid || '').trim();
    if (!uid) return false;
    if (input.firestoreReady !== true) return false;
    if (input.enabled === false) return false;

    const campaignId = getProductSplashCampaignId({
        campaignId: input.campaignId,
        id: input.id,
    });
    if (!campaignId) return false;

    const sku = String(input.productSku || '').trim();
    if (!sku) return false;

    if (!isProductSplashShopPath(input.pathname)) return false;
    if (isProductSplashStartPending(input.startDate, input.isoDate)) return false;

    const freq = normalizeAnnouncementDisplayFrequency(input.displayFrequency);
    const seenEntry = input.firestoreSeen && typeof input.firestoreSeen === 'object'
        ? input.firestoreSeen[campaignId]
        : null;
    if (isSeenForDisplayFrequency(seenEntry, freq, input.isoDate)) return false;

    return true;
}

/** Merge payload for users/{uid} (caller supplies serverTimestamp). */
export function buildProductSplashSeenMerge(campaignId, seenAt) {
    const id = sanitizeProductSplashCampaignId(campaignId);
    if (!id) return null;
    return {
        [PRODUCT_SPLASH_SEEN_FIELD]: {
            [id]: { seenAt: seenAt ?? null },
        },
    };
}
